import { createContext, type PointerEvent as ReactPointerEvent, type RefObject, use } from 'react';

export interface SidebarContextProps {
  state: 'expanded' | 'collapsed';
  /** Whether the rail is expanded. A drawer is always its full width, so it always reads open. */
  open: boolean;
  /** Which edge of the page the panel holds. */
  side: 'left' | 'right';
  /** Whether the panel floats over the page instead of standing beside it. */
  isDrawer: boolean;
  /** The shortcut that toggles the panel, for the trigger to name. Undefined when it has none. */
  keyboardShortcut: string | undefined;
  toggleSidebar: () => void;
  sidebarRef: RefObject<HTMLElement | null>;
  /** The panel's id, so a trigger outside it can be the popover's invoker. */
  sidebarId: string;
  /** A width restored from storage, which the panel carries inline. */
  initialWidth: number | undefined;
  /**
   * Drags the rail, resizing the panel until the pointer is released, and opening or shutting it
   * as the drag crosses the width it collapses at.
   */
  startResize: (event: ReactPointerEvent) => void;
  /** Widens or narrows the expanded panel by a step, for the rail's arrow keys. */
  resizeBy: (delta: number) => void;
}

export const SidebarContext = createContext<SidebarContextProps | null>(null);

export const useSidebar = () => {
  const context = use(SidebarContext);
  if (!context) {
    throw new Error('useSidebar must be used within a SidebarProvider.');
  }

  return context;
};
